import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FileVideo, FileAudio, FileText, Clock, Calendar, ChevronRight, Trash2, StopCircle, Loader2 } from "lucide-react";
import { formatDuration, formatDate, getStatusLabel } from "@/lib/utils";
import { toast } from "sonner";
import videoApi from "@/lib/api";
import { useUpload } from "@/context/UploadContext";
import { useLanguage } from "@/context/LanguageContext";

const VideoCard = ({ video, onDelete }) => {
  const [deleting, setDeleting] = useState(false);
  const { isUploading } = useUpload();
  const { language } = useLanguage();

  const isProcessing =
    video.status === "pending" ||
    video.status === "processing" ||
    video.status === "extracting_audio" ||
    video.status === "transcribing" ||
    video.status === "analyzing";

  const handleNavigationBlock = (e) => {
    if (isUploading) {
      e.preventDefault();
      toast.error(
        language === "es"
          ? "No puedes abrir otro video mientras se sube uno. Espera a que termine la subida."
          : "Cannot open another video while uploading. Wait for upload to complete."
      );
      return false;
    }
    return true;
  };

  const handleDelete = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!window.confirm("Are you sure you want to delete this video?")) {
      return;
    }

    setDeleting(true);
    try {
      await videoApi.delete(video.id);
      toast.success("Video deleted successfully");
      if (onDelete) onDelete(video.id);
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete video");
    } finally {
      setDeleting(false);
    }
  };

  const handleCancel = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!window.confirm("Are you sure you want to cancel this processing?")) {
      return;
    }

    setDeleting(true);
    try {
      await videoApi.cancel(video.id);
      toast.success("Processing cancelled");
      if (onDelete) onDelete(video.id);
    } catch (error) {
      console.error("Cancel error:", error);
      toast.error("Failed to cancel processing");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="relative group">
      <Link to={`/video/${video.id}`} onClick={handleNavigationBlock} data-testid={`video-card-${video.id}`}>
        <Card className="card-hover bg-card p-4 cursor-pointer">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
            <div className="w-12 h-12 rounded bg-primary/10 flex items-center justify-center shrink-0">
              {video.content_kind === "document" ? (
                <FileText className="w-6 h-6 text-primary" />
              ) : video.is_audio ? (
                <FileAudio className="w-6 h-6 text-primary" />
              ) : (
                <FileVideo className="w-6 h-6 text-primary" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-medium truncate mb-1">
                {video.original_filename}
              </h3>
              <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                {video.duration_seconds && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatDuration(video.duration_seconds)}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {formatDate(video.created_at, language)}
                </span>
              </div>
            </div>
            <div className="flex w-full items-center justify-between gap-3 sm:w-auto sm:justify-end">
              <Badge
                variant="outline"
                className={`status-${video.status} text-xs font-mono`}
              >
                {getStatusLabel(video.status, language)}
              </Badge>
              
              {/* Action Buttons */}
              <div className="flex items-center gap-1">
                {isProcessing && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-orange-500 hover:text-orange-600 hover:bg-orange-500/10"
                    onClick={handleCancel}
                    disabled={deleting}
                    title="Cancel processing"
                    data-testid={`cancel-btn-${video.id}`}
                  >
                    {deleting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <StopCircle className="w-4 h-4" />
                    )}
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={handleDelete}
                  disabled={deleting}
                  title="Delete video"
                  data-testid={`delete-btn-${video.id}`}
                >
                  {deleting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </Button>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </div>
          </div>

          {video.status === "completed" && video.key_moments && (
            <div className="mt-4 pt-4 border-t border-border">
              <p className="text-xs font-mono text-muted-foreground mb-2 uppercase tracking-wider">
                Key Moments
              </p>
              <div className="flex flex-wrap gap-2">
                {video.key_moments.slice(0, 3).map((moment, idx) => (
                  <span key={idx} className="timestamp-pill">
                    {moment.reference_label || moment.timestamp}
                  </span>
                ))}
                {video.key_moments.length > 3 && (
                  <span className="text-xs text-muted-foreground">
                    +{video.key_moments.length - 3} more
                  </span>
                )}
              </div>
            </div>
          )}

          {isProcessing && (
            <div className="mt-4 pt-4 border-t border-border">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                  <span className="text-sm text-blue-400">
                    {getStatusLabel(video.status, language)}...
                  </span>
                </div>
                <span className="text-sm font-medium text-foreground" data-testid={`video-progress-${video.id}`}>
                  {video.progress || 0}%
                </span>
              </div>
              <div className="mt-3 h-2 w-full rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-500 ease-out"
                  style={{ width: `${video.progress || 0}%` }}
                />
              </div>
              {video.processing_message && (
                <p className="mt-2 text-xs text-muted-foreground" data-testid={`video-processing-message-${video.id}`}>
                  {video.processing_message}
                </p>
              )}
            </div>
          )}

          {video.status === "failed" && (
            <div className="mt-4 pt-4 border-t border-border">
              <p className="text-sm text-red-400 truncate">
                {video.error_message || (language === "es" ? "Procesamiento fallido" : "Processing failed")}
              </p>
            </div>
          )}
        </Card>
      </Link>
    </div>
  );
};

export default VideoCard;
